import { getLoadcaseX } from "@common/helpers/protoGen";
import { ng } from "@common/ngds";

/////////////////////////////////////////////////////////////////////////////
// exported classes

class ColorObject {
  x: number = 1; // red (0-1)
  y: number = 1; // green (0-1)
  z: number = 1; // blue (0-1)
  hex: string = "#ffffff";
}

// each value range from 0-1
type ColorFloatArray = [number, number, number];

class Rgb {
  r: number = 0; // red (0-255)
  g: number = 0; // green (0-255)
  b: number = 0; // blue (0-255)

  /**
   * Each value range from 0 - 255
   */
  constructor(r: number, g: number, b: number) {
    this.r = r;
    this.g = g;
    this.b = b;
  }

  // -----------------------------------------------------------------------

  /**
   * @param g the gray value, 0 - 255
   */
  static fromGrayInt = (g: number) => {
    return new Rgb(g, g, g);
  };

  // -----------------------------------------------------------------------
  // [R, G, B] RGB stored as floats (0-1)

  static fromFloatArray = (arr: ColorFloatArray) => {
    let r = Math.round(arr[0] * 255);
    let g = Math.round(arr[1] * 255);
    let b = Math.round(arr[2] * 255);
    return new Rgb(r, g, b);
  };

  toFloatArray = (): ColorFloatArray => {
    return [this.r / 255.0, this.g / 255.0, this.b / 255.0];
  };

  // -----------------------------------------------------------------------
  // "#RRGGBB" RGB stored as hexadecimals

  static fromHex = (hex: string) => {
    var colorComponentsAsHex = hex.substring(1).match(/.{1,2}/g);
    if (colorComponentsAsHex && colorComponentsAsHex.length > 2) {
      return new Rgb(
        parseInt(colorComponentsAsHex[0], 16),
        parseInt(colorComponentsAsHex[1], 16),
        parseInt(colorComponentsAsHex[2], 16),
      );
    } else {
      // invalid hex
      return INVALID_COLOR;
    }
  };

  toHex = () => {
    return (
      "#" +
      [this.r, this.g, this.b]
        // convert each component to hex
        .map((n) => Math.round(n).toString(16).padStart(2, "0"))
        // join the components
        .join("")
    );
  };

  // -----------------------------------------------------------------------
  // custom color structure

  static fromColorObject = (obj: ColorObject) => {
    return new Rgb(Math.round(obj.x * 255), Math.round(obj.y * 255), Math.round(obj.z * 255));
  };

  toColorObject = () => {
    return {
      x: this.r / 255.0,
      y: this.g / 255.0,
      z: this.b / 255.0,
      hex: this.toHex(),
    } as ColorObject;
  };

  // -----------------------------------------------------------------------
  // rgb(R, G, B)

  static fromRgbString = (str: string) => {
    const components = str
      // skip leading rgb
      .substring(3)
      // remove characters that are not numbers
      .split(/[,() ]/)
      // trim
      .map((s) => s.trim())
      // remove empty strings
      .filter((s) => s.length > 0)
      // convert to numbers
      .map((s) => parseInt(s, 10));
    if (components.length === 3) {
      return new Rgb(components[0], components[1], components[2]);
    } else {
      // invalid rgb: return yellow
      return INVALID_COLOR;
    }
  };

  toRgbString = () => {
    let ret = `rgb(${this.r}, ${this.g}, ${this.b})`;
    return ret;
  };
}

/////////////////////////////////////////////////////////////////////////////
// known colors

export const INVALID_COLOR = new Rgb(255, 255, 0);

// grid
const GRID_BAKGROUND_COLOR_LIGHT = Rgb.fromGrayInt(250);
const GRID_MINOR_LINE_COLOR_LIGHT = Rgb.fromGrayInt(220);
const GRID_MAJOR_LINE_COLOR_LIGHT = Rgb.fromGrayInt(200);

const GRID_BAKGROUND_COLOR_DARK = Rgb.fromGrayInt(38);
const GRID_MINOR_LINE_COLOR_DARK = Rgb.fromGrayInt(48);
const GRID_MAJOR_LINE_COLOR_DARK = Rgb.fromGrayInt(74);

// graphs
const GRAPH_POSITIVE_COLOR_LIGHT_DEFAULT = Rgb.fromFloatArray([0.88, 0.619, 0.25]);
const GRAPH_NEGATIVE_COLOR_LIGHT_DEFAULT = Rgb.fromFloatArray([0.113, 0.35, 0.6]);
const GRAPH_POSITIVE_COLOR_DARK_DEFAULT = Rgb.fromFloatArray([0.88, 0.619, 0.25]);
const GRAPH_NEGATIVE_COLOR_DARK_DEFAULT = Rgb.fromFloatArray([0.113, 0.35, 0.6]);

// https://yeun.github.io/open-color/
const GREY_3 = "#dee2e6";
const GREY_8 = "#343a40";
const RED_3 = "#ffa8a8";
const RED_8 = "#e03131";
const PINK_3 = "#faa2c1";
const PINK_8 = "#c2255c";
const GRAPE_3 = "#e599f7";
const GRAPE_8 = "#9c36b5";
const VIOLET_3 = "#b197fc";
const VIOLET_8 = "#6741d9";
const INDIGO_3 = "#91a7ff";
const INDIGO_8 = "#3b5bdb";
const BLUE_3 = "#74c0fc";
const BLUE_8 = "#1971c2";
const CYAN_3 = "#66d9e8";
const CYAN_8 = "#0c8599";
const TEAL_3 = "#63e6be";
const TEAL_8 = "#099268";
const GREEN_3 = "#8ce99a";
const GREEN_8 = "#2f9e44";
const LIME_3 = "#c0eb75";
const LIME_8 = "#66a80f";
const YELLOW_3 = "#ffe066";
const YELLOW_8 = "#f08c00";
const ORANGE_3 = "#ffc078";
const ORANGE_8 = "#e8590c";

const GRAPH_POSITIVE_COLOR_LIGHT_N = Rgb.fromHex(RED_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_N = Rgb.fromHex(RED_8);
const GRAPH_POSITIVE_COLOR_DARK_N = Rgb.fromHex(RED_3);
const GRAPH_NEGATIVE_COLOR_DARK_N = Rgb.fromHex(RED_8);

const GRAPH_POSITIVE_COLOR_LIGHT_M = Rgb.fromHex(BLUE_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_M = Rgb.fromHex(BLUE_8);
const GRAPH_POSITIVE_COLOR_DARK_M = Rgb.fromHex(BLUE_3);
const GRAPH_NEGATIVE_COLOR_DARK_M = Rgb.fromHex(BLUE_8);

const GRAPH_POSITIVE_COLOR_LIGHT_V = Rgb.fromHex(GREEN_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_V = Rgb.fromHex(GREEN_8);
const GRAPH_POSITIVE_COLOR_DARK_V = Rgb.fromHex(GREEN_3);
const GRAPH_NEGATIVE_COLOR_DARK_V = Rgb.fromHex(GREEN_8);

const GRAPH_POSITIVE_COLOR_LIGHT_D = Rgb.fromHex(GRAPE_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_D = Rgb.fromHex(GRAPE_8);
const GRAPH_POSITIVE_COLOR_DARK_D = Rgb.fromHex(GRAPE_3);
const GRAPH_NEGATIVE_COLOR_DARK_D = Rgb.fromHex(GRAPE_8);

const GRAPH_POSITIVE_COLOR_LIGHT_R = Rgb.fromHex(ORANGE_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_R = Rgb.fromHex(ORANGE_8);
const GRAPH_POSITIVE_COLOR_DARK_R = Rgb.fromHex(ORANGE_3);
const GRAPH_NEGATIVE_COLOR_DARK_R = Rgb.fromHex(ORANGE_8);

const GRAPH_POSITIVE_COLOR_LIGHT_WK = Rgb.fromHex(YELLOW_3);
const GRAPH_NEGATIVE_COLOR_LIGHT_WK = Rgb.fromHex(YELLOW_8);
const GRAPH_POSITIVE_COLOR_DARK_WK = Rgb.fromHex(YELLOW_3);
const GRAPH_NEGATIVE_COLOR_DARK_WK = Rgb.fromHex(YELLOW_8);

// color brewer (qualitative): 12-class Paired
// https://colorbrewer2.org/#type=qualitative&scheme=Paired&n=12
const COLORS_ORIGINAL_ORDER = [
  new Rgb(166, 206, 227), // light blue
  new Rgb(31, 120, 180), // dark blue
  new Rgb(178, 223, 138), // light green
  new Rgb(51, 160, 44), // dark green

  // here we enter color blind unfriendly territory
  new Rgb(251, 154, 153), // light red
  new Rgb(227, 26, 28), // dark red
  new Rgb(253, 191, 111), // light orange

  // here we drop print friendly
  new Rgb(255, 127, 0), // dark orange

  // here we aren't color blind friendly
  new Rgb(202, 178, 214), // light purple
  new Rgb(106, 61, 154), // dark purple

  // here we enter into lcd unfriendly territory
  new Rgb(255, 255, 153), // light yellow

  // here we are lcd unfriendly too
  new Rgb(177, 89, 40), // dark brown
];

const color_dark_orange = new Rgb(255, 127, 0);
const color_light_blue = new Rgb(166, 206, 227);
const color_dark_purple = new Rgb(106, 61, 154);
const color_light_green = new Rgb(178, 223, 138);
const color_dark_red = new Rgb(227, 26, 28);
const color_light_yellow = new Rgb(255, 255, 153);
const color_dark_brown = new Rgb(177, 89, 40);
const color_light_red = new Rgb(251, 154, 153);
const color_dark_blue = new Rgb(31, 120, 180);
const color_light_orange = new Rgb(253, 191, 111);
const color_dark_green = new Rgb(51, 160, 44);
const color_light_purple = new Rgb(202, 178, 214);
const color_light_violet = new Rgb(177, 151, 252);
const color_dark_teal = new Rgb(0, 128, 128);

// Loadcase pastel colors
const color_regatta_blue = Rgb.fromHex("#517cb9");
const color_rinsing_green = Rgb.fromHex("#6ec6c5");
const color_dusky_citron = Rgb.fromHex("#e2cc7d");
const color_aqua_grey = Rgb.fromHex("#a8b2a9");
const color_cosmic_sky = Rgb.fromHex("#aaacc5");
const color_cosmic_sky_light = Rgb.fromHex("#d8d8e9");
const color_alaskan_blue = Rgb.fromHex("#76a9d4");
const color_veiled_vista = Rgb.fromHex("#c7e5ca");
const color_nantucket_breeze = Rgb.fromHex("#bad1eb");
const color_cloud_dancer = Rgb.fromHex("#e7e5df");

// Static list of colors tied to loadcase type)
const LOADCASE_COLORS: Record<ng.loadcase.Type, Rgb> = {
  [ng.loadcase.Type.TYPE_PERMANENT_LOAD]: color_regatta_blue,
  [ng.loadcase.Type.TYPE_IMPOSED_LOAD]: color_rinsing_green,
  [ng.loadcase.Type.TYPE_SELF_WEIGHT]: color_aqua_grey,
  [ng.loadcase.Type.TYPE_WIND_LOAD]: color_dusky_citron,
  [ng.loadcase.Type.TYPE_SNOW_LOAD]: color_cosmic_sky,
  [ng.loadcase.Type.TYPE_ICE_LOAD]: color_cosmic_sky_light,
  [ng.loadcase.Type.TYPE_SOIL_LOAD]: color_dark_brown,
  [ng.loadcase.Type.TYPE_ACCIDENT_LOAD]: color_alaskan_blue,
  [ng.loadcase.Type.TYPE_SEISMIC_LOAD]: color_dark_blue,
  [ng.loadcase.Type.TYPE_TEMPERATURE_LOAD]: color_light_orange,
  [ng.loadcase.Type.TYPE_SOIL_SELF_WEIGHT]: color_dark_green,
  [ng.loadcase.Type.TYPE_CONSTRUCTION_LOAD]: color_light_purple,
  [ng.loadcase.Type.TYPE_PERMANENT_VARIABLE_LOAD]: color_light_violet,
  [ng.loadcase.Type.TYPE_COMPACTION]: color_cloud_dancer,
  [ng.loadcase.Type.TYPE_ACCIDENTAL_GROUND_WATER]: color_nantucket_breeze,
  [ng.loadcase.Type.TYPE_UNSPECIFIED]: color_dark_teal,
};

const COLORS = [
  color_dark_orange,
  color_light_blue,
  color_dark_purple,
  color_light_green,
  color_dark_red,
  color_light_yellow,
  color_dark_brown,
  color_light_red,
  color_dark_blue,
  color_light_orange,
  color_dark_green,
  color_light_purple,
  color_dark_teal,
  color_light_violet,
  color_regatta_blue,
  color_rinsing_green,
  color_dusky_citron,
  color_aqua_grey,
  color_cosmic_sky,
  color_cosmic_sky_light,
  color_alaskan_blue,
  color_veiled_vista,
  color_nantucket_breeze,
  color_cloud_dancer,
];

// Compare Function

/**
 * This function takes the color in RGB format as input
 *  and compares its lightness (L) to the specified threshold.
 * Adjust the threshold value to suit your needs.
 * Values closer to 0 represent darker colors,
 * while values closer to 1 represent lighter colors.
 * */
const isColorDarkerThan = (rgb: { r: number; g: number; b: number }, threshold: number): boolean => {
  // Calculate luminance
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  // Compare luminance to threshold
  return l < threshold;
};

// A GOOD OL' FROM HEX TO RGB CONVERTER
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  // Remove '#' if present
  hex = hex.replace("#", "");

  // Parse hex values into r, g, b components
  let r: number, g: number, b: number;
  if (hex.length === 3) {
    // Expand short hex format (e.g., #RGB) to full format (e.g., #RRGGBB)
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    // Full hex format (e.g., #RRGGBB)
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    throw new Error("Invalid hex color format. Expected 3 or 6 characters.");
  }

  // Return RGB object
  return { r, g, b };
};

/////////////////////////////////////////////////////////////////////////////
// exported "namespaced" functions

class Colors {
  // -----------------------------------------------------------------------
  // shader functions

  // static set_vec3(material: THREE.ShaderMaterial, name: string, val: [number, number, number]) {
  //   let uni = material.uniforms[name];
  //   if (uni === undefined) {
  //     return;
  //   }
  //   uni.value.x = val[0];
  //   uni.value.y = val[1];
  //   uni.value.z = val[2];
  // }

  // -----------------------------------------------------------------------
  // loadcase functions

  // ___________________________________________________________________
  // DYNAMIC COLOR HANDLING (i.e. color is chosen from an available slot on a color palette)

  // get current color for a loadcase, or null if not found
  static loadcases_lookupColor = (project: ng.frontend.column.Project, guid: string) => {
    const loadcases = project?.settings?.visuals?.loadcases;
    if (!loadcases) return null;

    const lc = loadcases.find((lc) => lc?.guid === guid);
    if (!lc) return null;

    const stringColor = lc.color;
    if (!stringColor) return null;

    return Rgb.fromRgbString(stringColor);
  };

  // removes the corresponding color for a loadcase
  static loadcases_removeColor = (project: ng.frontend.column.Project, guid: string) => {
    const loadcases = project!.settings!.visuals!.loadcases!;

    const index = loadcases.findIndex((lc) => lc.guid === guid);
    if (index < 0) return;

    loadcases.splice(index, 1);
  };

  // deletes loadcase color from visuals related ot given guid
  static loadcases_deleteColor = (project: ng.frontend.column.Project, guid: string) => {
    const loadcases = project!.settings!.visuals!.loadcases!;

    const index = loadcases?.findIndex((lc: ng.frontend.column.ILoadCaseVisuals) => lc?.guid === guid);

    if (index != null && index > -1) {
      loadcases?.splice(index, 1);
    }
  };

  // ___________________________________________________________________
  // STATIC COLOR HANDLING (i.e. color are mapped to loadcase types 1:1)

  static loadcases_getColor = (project: ng.frontend.column.Project, guid: string) => {
    if (!guid || !project) {
      return LOADCASE_COLORS[ng.loadcase.Type.TYPE_UNSPECIFIED];
    }

    const structure = project.data?.input?.structures?.[0];
    if (!structure) {
      return LOADCASE_COLORS[ng.loadcase.Type.TYPE_UNSPECIFIED];
    }

    // Plain-object lookup first to handle Redux-serialized project data.
    const loadcases = structure.loading?.loadcases;
    const plainLoadcase = loadcases?.find((lc) => lc?.id?.guid === guid);
    if (plainLoadcase) {
      const lcType = ng.loadcase.Type_FromObject(plainLoadcase.type);
      return LOADCASE_COLORS[lcType] ?? LOADCASE_COLORS[ng.loadcase.Type.TYPE_UNSPECIFIED];
    }

    // Keep legacy proto-based fallback for callers that provide proto objects.
    const protoLoadcase = getLoadcaseX(guid, structure);
    if (protoLoadcase) {
      return LOADCASE_COLORS[protoLoadcase.type] ?? LOADCASE_COLORS[ng.loadcase.Type.TYPE_UNSPECIFIED];
    }

    // Final fallback: if no loadcase exists, use any persisted visuals color.
    const visualsColor = Colors.loadcases_lookupColor(project, guid);
    return visualsColor ?? LOADCASE_COLORS[ng.loadcase.Type.TYPE_UNSPECIFIED];
  };

  static loadcases_getVisualsColor = (project: ng.frontend.column.Project) => {
    const structures = project.data?.input?.structures;
    if (!structures) return [];

    const loadcases = structures[0]?.loading?.loadcases;
    if (!loadcases) return [];

    // Gather all loadcase guids
    const allGuids = loadcases.map((lc) => lc.id?.guid).filter((guid) => !!guid) as string[];

    // Create visuals for all loadcases
    return allGuids.map((guid) =>
      ng.frontend.column.LoadCaseVisuals.create({
        color: Colors.loadcases_getColor(project, guid).toRgbString(),
        guid: guid,
        visible: true,
      }),
    );
  };
}

export const loadcases_getColor = (project: ng.frontend.column.Project, guid: string): Rgb => {
  return Colors.loadcases_getColor(project, guid);
};

export const loadcases_getVisualsColor = (project: ng.frontend.column.Project) => {
  return Colors.loadcases_getVisualsColor(project);
};
